import React from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  DashboardSquare01Icon, 
  Logout01Icon,
  Sun01Icon,
  Moon02Icon,
  Home01Icon,
  Clock01Icon,
  UserCircleIcon,
  HelpCircleIcon,
  Money01Icon
} from '@hugeicons/core-free-icons';
import BottomNavbar from './BottomNavbar';
import { useAuth } from '../contexts/AuthContext';
import BalanceWidget from './BalanceWidget';
import MobileMenu from './MobileMenu';
import { api } from '../utils/api';

const ClientNavbar = ({ isDark, toggleTheme, rewardsCount, pendingCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  const navItems = [
    { name: 'Home', path: '/', icon: Home01Icon },
    { name: 'My Orders', path: '/requests', icon: DashboardSquare01Icon },
    { name: 'Rewards', path: '/commissions', icon: Money01Icon },
    { name: 'History', path: '/history', icon: Clock01Icon },
  ];

  return (
    <nav className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md border-b border-light-gray/20 dark:border-dark-border/20 sticky top-0 z-50 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center gap-3">
              <img src="/Safzi-Logo.png" alt="Safzi" className="h-8 w-auto" />
              <span className="text-2xl font-bold text-soft-green tracking-tight hidden sm:block font-mono">SAFZI</span>
            </Link>
            <div className="hidden lg:ml-8 lg:flex lg:space-x-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`inline-flex items-center px-4 py-2 rounded-3xl text-sm font-bold transition-all duration-300 relative ${
                      isActive
                        ? 'bg-soft-green/10 text-soft-green dark:text-light-mint shadow-sm'
                        : 'text-muted-gray hover:bg-gray-100/80 dark:hover:bg-dark-surface/50 hover:text-charcoal dark:hover:text-white'
                    }`}
                  >
                    <HugeiconsIcon icon={Icon} size={18} className="mr-2" />
                    {item.name}
                    {item.name === 'Rewards' && rewardsCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white shadow-lg border-2 border-white dark:border-dark-surface">
                        {rewardsCount}
                      </span>
                    )}
                    {item.name === 'My Orders' && pendingCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white shadow-lg border-2 border-white dark:border-dark-surface">
                        {pendingCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <BalanceWidget />
            
            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/support" className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 hover:text-soft-green transition-all" title="Support">
                <HugeiconsIcon icon={HelpCircleIcon} size={20} className="dark:text-gray-300" />
              </Link>
              <button 
                onClick={toggleTheme}
                className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 transform hover:-translate-y-1 hover:shadow-float transition-all duration-300 ease-in-out"
              >
                <HugeiconsIcon icon={isDark ? Sun01Icon : Moon02Icon} size={20} className="dark:text-gray-300" />
              </button>
              <Link to="/profile" className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 transform hover:-translate-y-1 hover:shadow-float transition-all duration-300 ease-in-out">
                <HugeiconsIcon icon={UserCircleIcon} size={20} className="dark:text-gray-300" />
              </Link>
              <button onClick={handleLogout} className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 transform hover:-translate-y-1 hover:shadow-float transition-all duration-300 ease-in-out">
                <HugeiconsIcon icon={Logout01Icon} size={20} className="dark:text-gray-300" />
              </button>
            </div>

            {/* Mobile Menu */}
            <MobileMenu isDark={isDark} toggleTheme={toggleTheme} />
          </div>
        </div>
      </div>
    </nav>
  );
};

const ClientLayout = ({ isDark, toggleTheme }) => {
  const [rewardsCount, setRewardsCount] = React.useState(0);
  const [pendingCount, setPendingCount] = React.useState(0);
React.useEffect(() => {
  const fetchData = async () => {
    try {
      // Fetch rewards
      const commRes = await api.get('/client/commissions');
      const earnings = commRes.data?.data?.earnings || commRes.data?.earnings || commRes.data?.data || [];
      const activeBalances = Array.isArray(earnings)
        ? earnings.filter(b => (b.balance || b.amount || 0) > 0)
        : [];
      setRewardsCount(activeBalances.length);

      // Fetch pending orders
      const txRes = await api.get('/client/transactions/pending-count');
      const count = txRes.data?.count ?? txRes.count ?? txRes.data?.data?.count ?? 0;
      setPendingCount(count);
    } catch (err) { 
      console.warn('Failed to fetch navigation counts:', err); 
    }
  };
  fetchData();
  const interval = setInterval(fetchData, 10000);
  return () => clearInterval(interval);
}, []);

  return (
    <div className="min-h-screen bg-off-white dark:bg-dark-bg transition-colors duration-200">
      <ClientNavbar isDark={isDark} toggleTheme={toggleTheme} rewardsCount={rewardsCount} pendingCount={pendingCount} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        <Outlet />
      </main>
      <BottomNavbar rewardsCount={rewardsCount} pendingCount={pendingCount} />
    </div>
  );
};

export default ClientLayout;
